import React from 'react';
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube } from 'lucide-react';
import { mockSocialLinks } from '../mock';
import { useTranslation } from '../hooks/useTranslation';
import AdBanner from '../components/AdBanner';
import { usePageSEO } from '../components/SEO';

const Contatti = () => {
  const { t, language } = useTranslation();
  usePageSEO({
    it: {
      title: 'Contatti — Studi di Melbourne e Sydney | Rete Italia',
      description: 'Contatta Rete Italia: email, telefono e gli indirizzi degli studi di Melbourne e Sydney. Scrivici o vieni a trovarci.',
    },
    en: {
      title: 'Contact — Melbourne & Sydney Studios | Rete Italia',
      description: 'Contact Rete Italia: email, phone and the addresses of our Melbourne and Sydney studios.',
    },
    basePath: '/contatti',
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-24 pb-16">
      <div className="container mx-auto px-4">
        {/* Advertisement Banner */}
        <AdBanner className="mb-8" />

        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-[#009246] via-[#CE2B37] to-[#00247D] bg-clip-text text-transparent">
              {t('contact.title')}
            </span>
          </h1>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form - JotForm Embed */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <iframe
              id="JotFormIFrame-253557197668071"
              title="Invia un Messaggio"
              src="https://form.jotform.com/253557197668071"
              style={{ width: '100%', minHeight: '1050px', border: 'none' }}
            />
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            {/* Contact Details */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold mb-6 text-gray-900">{t('contact.contactInfo')}</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#009246] to-[#00b359] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('contact.emailLabel')}</h3>
                    <a href={`mailto:${t('contact.emailAddress')}`} className="text-gray-600 hover:text-[#009246] transition-colors">
                      {t('contact.emailAddress')}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#CE2B37] to-[#e63946] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('contact.phoneLabel')}</h3>
                    <a href={`tel:${t('contact.phoneNumber').replace(/\s/g, '')}`} className="text-gray-600 hover:text-[#CE2B37] transition-colors text-lg font-semibold">
                      {t('contact.phoneNumber')}
                    </a>
                    <p className="text-gray-600 text-sm mt-1">{t('contact.phoneHours')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#25D366] to-[#128C7E] rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">WhatsApp</h3>
                    <a href="https://wa.me/61493383144" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#25D366] transition-colors text-lg font-semibold">
                      +61 493 383 144
                    </a>
                    <p className="text-gray-600 text-sm mt-1">{language === 'it' ? 'Centro Messaggi' : 'Message Center'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#00247D] to-[#0047AB] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">{t('contact.addressLabel')}</h3>
                    <div className="space-y-4">
                      <div>
                        <p className="font-semibold text-gray-900">{t('contact.melbourneStudio')}</p>
                        <a href="tel:0394810666" className="text-gray-600 hover:text-[#009246] text-sm font-medium">Tel 03 9481 0666</a>
                        <p className="text-gray-600 text-sm">35-37 Melville Road</p>
                        <p className="text-gray-600 text-sm">Brunswick West VIC 3055</p>
                        <p className="text-gray-600 text-sm">Australia</p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{t('contact.sydneyStudio')}</p>
                        <a href="tel:0295694522" className="text-gray-600 hover:text-[#009246] text-sm font-medium">Tel 02 9569 4522</a>
                        <p className="text-gray-600 text-sm">194-196 Norton St</p>
                        <p className="text-gray-600 text-sm">Leichhardt NSW 2040</p>
                        <p className="text-gray-600 text-sm">Australia</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-gradient-to-br from-[#009246] to-[#00b359] rounded-2xl shadow-xl p-8 text-white">
              <h2 className="text-2xl font-bold mb-6">{t('contact.followUs')}</h2>
              <p className="mb-6 opacity-90">
                {t('contact.followUsDesc')}
              </p>
              <div className="grid grid-cols-3 gap-4">
                <a
                  href={mockSocialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm p-4 rounded-xl flex items-center gap-3 transition-all duration-300 hover:scale-105"
                >
                  <Facebook className="w-6 h-6" />
                  <span className="font-semibold">Facebook</span>
                </a>
                <a
                  href={mockSocialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm p-4 rounded-xl flex items-center gap-3 transition-all duration-300 hover:scale-105"
                >
                  <Instagram className="w-6 h-6" />
                  <span className="font-semibold">Instagram</span>
                </a>
                <a
                  href={mockSocialLinks.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm p-4 rounded-xl flex items-center gap-3 transition-all duration-300 hover:scale-105"
                >
                  <Youtube className="w-6 h-6" />
                  <span className="font-semibold">YouTube</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contatti;